import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { CalendarIcon } from "@heroicons/react/24/solid";
import { PeriodCategory, PeriodOption } from "@/features/Metrics/Metrics.types";
import { HISTORICAL_PERIOD_CATEGORIES } from "@/features/Metrics/constants/PeriodCategories.constants";
import { PeriodRange } from "@/features/Metrics/PeriodRange";

interface PeriodSelectorMenuProps {
    selectedOption: PeriodOption;
    onSelect: (option: PeriodOption) => void;
    categories?: PeriodCategory[];
    allowedPeriodRange: PeriodRange | null;
}

export function PeriodSelectorMenu({
    categories = HISTORICAL_PERIOD_CATEGORIES,
    onSelect,
    selectedOption,
    allowedPeriodRange,
}: PeriodSelectorMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(
        null,
    );
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = () => {
        if (isOpen) {
            setHoveredCategoryId(null);
        } else {
            setHoveredCategoryId(selectedOption.resolution);
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setHoveredCategoryId(null);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const handleCategoryHover = (categoryId: string) => {
        setHoveredCategoryId(categoryId);
    };

    const handleOptionClick = (option: PeriodOption) => {
        onSelect(option);
        setIsOpen(false);
        setHoveredCategoryId(null);
    };

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={toggleMenu}
                style={{ background: "transparent" }}
                className={clsx(
                    "secondary sm",
                    "rounded-md border border-slate-600",
                    "body-micro subdued transition-colors hover:border-slate-500 hover:bg-slate-700",
                )}
            >
                <span className="flex items-center gap-2">
                    <CalendarIcon className="size-4 fill-gray-300" />
                    {`${selectedOption.resolution.charAt(0).toUpperCase() + selectedOption.resolution.slice(1)} - ${selectedOption.label}`}
                </span>
            </button>
            {isOpen && (
                <div
                    className={clsx(
                        "absolute right-0 top-full mt-1",
                        "z-50",
                        "flex items-start",
                    )}
                >
                    <div
                        className={clsx(
                            "w-[180px]",
                            "rounded-l-lg bg-slate-800 shadow-400",
                            "flex flex-col",
                        )}
                    >
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onMouseEnter={() =>
                                    handleCategoryHover(category.id)
                                }
                                className={clsx(
                                    "period-selector-category",
                                    "flex items-center justify-between px-4 py-3",
                                    "body-s text-left transition-colors",
                                    hoveredCategoryId === category.id
                                        ? "bg-slate-700 text-white"
                                        : "text-gray-300 hover:bg-slate-700/50",
                                )}
                            >
                                <span>{category.label}</span>
                                <ChevronRightIcon className="size-4" />
                            </button>
                        ))}
                    </div>

                    {hoveredCategoryId &&
                        (() => {
                            const category = categories.find(
                                (c) => c.id === hoveredCategoryId,
                            );
                            if (!category) return null;

                            return (
                                <div
                                    className={clsx(
                                        "w-[220px]",
                                        "rounded-r-lg bg-slate-800 shadow-400",
                                        "flex flex-col p-2",
                                    )}
                                >
                                    {category.options.map((option) => {
                                        const isSelected =
                                            selectedOption.resolution ===
                                                category.id &&
                                            selectedOption.id === option.id;
                                        const isDisabled =
                                            option.isDisabled(
                                                allowedPeriodRange,
                                            );

                                        return (
                                            <button
                                                key={option.id}
                                                onClick={() => {
                                                    if (!isDisabled) {
                                                        handleOptionClick(
                                                            option,
                                                        );
                                                    }
                                                }}
                                                disabled={isDisabled}
                                                className={clsx(
                                                    "period-selector-option",
                                                    "flex items-center gap-3 rounded px-3 py-2.5",
                                                    "body-s text-left transition-colors",
                                                    isDisabled
                                                        ? "cursor-not-allowed opacity-40"
                                                        : "hover:bg-slate-700",
                                                )}
                                            >
                                                <div
                                                    className={clsx(
                                                        "flex size-4 shrink-0 items-center justify-center rounded-full border-2",
                                                        isSelected
                                                            ? "border-blue-500"
                                                            : "border-gray-500",
                                                    )}
                                                >
                                                    {isSelected && (
                                                        <div className="size-2 rounded-full bg-blue-500" />
                                                    )}
                                                </div>
                                                <span
                                                    className={clsx(
                                                        "flex-1",
                                                        isSelected
                                                            ? "text-white"
                                                            : "text-gray-300",
                                                    )}
                                                >
                                                    {option.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                </div>
            )}
        </div>
    );
}
